
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  Globe, 
  HelpCircle, 
  Info, 
  Mail, 
  Upload, 
  Users, 
  Layout, 
  Eye,
  Share2 
} from 'lucide-react';
import { BusinessSettings } from '@/utils/businessService';
import FAQEditor from '@/components/business/FAQEditor';
import HowItWorksEditor from '@/components/business/HowItWorksEditor';
import TermsEditor from '@/components/business/TermsEditor';
import PrivacyPolicyEditor from '@/components/business/PrivacyPolicyEditor';
import ContactSettingsForm from '@/components/business/ContactSettingsForm';
import LogoUpload from '@/components/business/LogoUpload';
import ManagersEditor from '@/components/business/ManagersEditor';
import DomainSettingsTab from '@/components/business/DomainSettingsTab';
import LandingPageEditor from '@/components/business/LandingPageEditor';
import SocialMediaSettings from '@/components/business/SocialMediaSettings';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface BusinessSettingsTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  settings: BusinessSettings | null;
  onSettingsSaved: () => Promise<void>;
}

const BusinessSettingsTabs: React.FC<BusinessSettingsTabsProps> = ({
  activeTab,
  setActiveTab,
  settings,
  onSettingsSaved
}) => {
  const navigate = useNavigate();
  
  const handlePreviewLanding = () => {
    // Open in a new tab to avoid disrupting the admin session
    window.open('/business-custom-landing', '_blank');
  };
  
  return (
    <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="flex justify-between items-center mb-4">
        <TabsList className="grid w-full grid-cols-10">
          <TabsTrigger value="landing" className="flex items-center">
            <Layout className="mr-2 h-4 w-4" />
            <span>Landing</span>
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center">
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>FAQ</span>
          </TabsTrigger>
          <TabsTrigger value="howItWorks" className="flex items-center">
            <Info className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">How It Works</span>
            <span className="sm:hidden">How</span>
          </TabsTrigger>
          <TabsTrigger value="terms" className="flex items-center">
            <FileText className="mr-2 h-4 w-4" />
            <span>Terms</span>
          </TabsTrigger>
          <TabsTrigger value="privacy" className="flex items-center">
            <FileText className="mr-2 h-4 w-4" />
            <span>Privacy</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center">
            <Mail className="mr-2 h-4 w-4" />
            <span>Contact</span>
          </TabsTrigger>
          <TabsTrigger value="logo" className="flex items-center">
            <Upload className="mr-2 h-4 w-4" />
            <span>Logo</span>
          </TabsTrigger>
          <TabsTrigger value="managers" className="flex items-center">
            <Users className="mr-2 h-4 w-4" />
            <span>Managers</span>
          </TabsTrigger>
          <TabsTrigger value="domain" className="flex items-center">
            <Globe className="mr-2 h-4 w-4" />
            <span>Domain</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center">
            <Share2 className="mr-2 h-4 w-4" />
            <span>Social</span>
          </TabsTrigger>
        </TabsList>
        <Button 
          variant="outline" 
          className="ml-4 flex items-center"
          onClick={handlePreviewLanding}
        >
          <Eye className="mr-2 h-4 w-4" />
          Preview Landing
        </Button>
      </div>
      
      <TabsContent value="landing" className="space-y-4">
        <LandingPageEditor 
          businessId={settings?.domain || 'default-business'} 
          onSaved={onSettingsSaved} 
        />
        <div className="flex justify-end mt-4">
          <Button 
            variant="outline" 
            className="flex items-center"
            onClick={handlePreviewLanding}
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview Landing
          </Button>
        </div>
      </TabsContent>
      
      <TabsContent value="faq" className="space-y-4">
        <FAQEditor 
          initialContent={settings?.faq || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="howItWorks" className="space-y-4">
        <HowItWorksEditor 
          initialContent={settings?.howItWorks || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="terms" className="space-y-4">
        <TermsEditor 
          initialContent={settings?.terms || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="privacy" className="space-y-4">
        <PrivacyPolicyEditor 
          initialContent={settings?.privacyPolicy || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="contact" className="space-y-4">
        <ContactSettingsForm 
          initialEmail={settings?.contactEmail || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="logo" className="space-y-4">
        <LogoUpload 
          currentLogo={settings?.logoUrl || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="managers" className="space-y-4">
        <ManagersEditor />
      </TabsContent>
      
      <TabsContent value="domain" className="space-y-4">
        <DomainSettingsTab 
          initialDomain={settings?.domain || ''} 
          onSaved={onSettingsSaved} 
        />
      </TabsContent>
      
      <TabsContent value="social" className="space-y-4">
        <SocialMediaSettings />
      </TabsContent>
    </Tabs>
  );
};

export default BusinessSettingsTabs;
